// Thrown from plain functions, not components — hence the i18next singleton
// rather than the useTranslation hook.
import { i18n } from "../../i18n";

// Auth: X-LOX24-AUTH-TOKEN header carrying the "clientId:token" API key.
// Docs: https://doc.lox24.eu/api/

const BASE_URL = "https://api.lox24.eu";

export class Lox24ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = "Lox24ApiError";
  }
}

export interface AccountInfo {
  id?: number;
  currency?: string;
  balance_amount?: number;
  balance_changed_at?: number;
  is_text_deleted?: boolean;
  services?: string[];
  groups?: unknown[];
  [key: string]: unknown;
}

/**
 * Identifies this integration to LOX24. Case-sensitive — don't reword it.
 */
const CLIENT_HEADER = "LOX24-Outlook/Calendar/Notifications";

function authHeaders(apiKey: string): HeadersInit {
  return {
    "X-LOX24-AUTH-TOKEN": apiKey.trim(),
    Accept: "application/json",
    "X-LOX24-CLIENT": CLIENT_HEADER,
  };
}

export async function getAccountInfo(apiKey: string): Promise<AccountInfo> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}/me`, { headers: authHeaders(apiKey) });
  } catch (err) {
    throw new Lox24ApiError(0, i18n.t("errors.lox24Network", { detail: (err as Error).message }));
  }

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    const message =
      response.status === 401
        ? i18n.t("errors.lox24Unauthorized")
        : i18n.t("errors.lox24Api", { status: response.status, detail }).trim();
    throw new Lox24ApiError(response.status, message);
  }

  return (await response.json()) as AccountInfo;
}

async function postJson(
  apiKey: string,
  path: string,
  body: Record<string, unknown>
): Promise<Record<string, unknown>> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: "POST",
      headers: { ...authHeaders(apiKey), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new Lox24ApiError(0, i18n.t("errors.lox24Network", { detail: (err as Error).message }));
  }
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    const message =
      response.status === 401
        ? i18n.t("errors.lox24UnauthorizedShort")
        : i18n.t("errors.lox24Api", { status: response.status, detail }).trim();
    throw new Lox24ApiError(response.status, message);
  }
  return (await response.json()) as Record<string, unknown>;
}

export interface SendSmsParams {
  senderId: string;
  phone: string;
  text: string;
  serviceCode: string; // "direct" | "text2speech"
  isUnicode: boolean;
  deliveryAt?: number; // unix seconds — scheduled send
}

export interface SmsResult {
  uuid: string;
  statusCode?: number;
  isSent?: boolean;
  parts?: number;
  price?: number;
}

/** Send a single SMS — POST /sms (REQ-MAN-005). */
export async function sendSms(apiKey: string, p: SendSmsParams): Promise<SmsResult> {
  const body: Record<string, unknown> = {
    sender_id: p.senderId,
    phone: p.phone,
    text: p.text,
    service_code: p.serviceCode,
    is_unicode: p.isUnicode,
  };
  if (p.deliveryAt) body.delivery_at = p.deliveryAt;
  const r = await postJson(apiKey, "/sms", body);
  return {
    uuid: String(r.uuid),
    statusCode: r.status_code as number | undefined,
    isSent: r.is_sent as boolean | undefined,
    parts: r.parts_count as number | undefined,
    price: r.price as number | undefined,
  };
}

/** Price/parts estimate without sending — POST /sms/dryrun. */
export async function dryRunSms(
  apiKey: string,
  p: SendSmsParams
): Promise<{ price?: number; parts?: number }> {
  const r = await postJson(apiKey, "/sms/dryrun", {
    sender_id: p.senderId,
    phone: p.phone,
    text: p.text,
    service_code: p.serviceCode,
    is_unicode: p.isUnicode,
  });
  return { price: r.price as number | undefined, parts: r.parts_count as number | undefined };
}

export interface SendBulkParams {
  senderId: string;
  phones: string[];
  text: string;
  serviceCode: string;
  isUnicode: boolean;
  deliveryAt?: number;
}

export interface BulkResult {
  uuid: string;
  statusCode?: number;
  total?: number;
  cost?: number;
}

/** Send to multiple recipients — POST /bulks (REQ-MAN-005). */
export async function sendBulk(apiKey: string, p: SendBulkParams): Promise<BulkResult> {
  const body: Record<string, unknown> = {
    sender_id: p.senderId,
    phones: p.phones,
    text: p.text,
    service_code: p.serviceCode,
    is_unicode: p.isUnicode,
  };
  if (p.deliveryAt) body.delivery_at = p.deliveryAt;
  const r = await postJson(apiKey, "/bulks", body);
  return {
    uuid: String(r.uuid),
    statusCode: r.status_code as number | undefined,
    total: r.msg_total_count as number | undefined,
    cost: r.msg_total_cost as number | undefined,
  };
}
